import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { User, Mail, Phone, Calendar, Settings, LogOut, Award } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function ProfilePage() {
  const { profile, signOut } = useAuth();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (!profile) {
    return (
      <>
        <Header />
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-4 text-sm text-gray-600">Loading...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gray-50 py-8">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
            <p className="mt-2 text-gray-600">Manage your account settings and preferences</p>
          </div>

          {/* Profile Card */}
          <div className="mb-6 rounded-xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-start gap-6">
              <div className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-full bg-gray-200">
                {profile.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    alt={profile.full_name || 'User'}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-blue-100 text-blue-600">
                    <User className="h-12 w-12" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900">
                  {profile.full_name || 'User'}
                </h2>
                <div className="mt-4 space-y-2 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <span>{profile.email}</span>
                  </div>
                  {profile.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      <span>{profile.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Award className="h-4 w-4" />
                    <span className="capitalize">{profile.role}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>Joined {formatDate(profile.created_at)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Role-Specific Actions */}
          {profile.role === 'client' && (
            <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Become a Trainer</h3>
              <p className="mb-4 text-sm text-gray-600">
                Apply to become a certified trainer and start earning by helping others achieve
                their fitness goals.
              </p>
              <Link
                to="/become-trainer"
                className="inline-block rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Apply Now
              </Link>
            </div>
          )}

          {profile.role === 'trainer' && (
            <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Trainer Dashboard</h3>
              <p className="mb-4 text-sm text-gray-600">
                Manage your bookings, availability, and earnings from your trainer dashboard.
              </p>
              <Link
                to="/dashboard/trainer"
                className="inline-block rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Go to Dashboard
              </Link>
            </div>
          )}

          {profile.role === 'admin' && (
            <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <h3 className="mb-4 text-lg font-semibold text-gray-900">Admin Dashboard</h3>
              <p className="mb-4 text-sm text-gray-600">
                Manage trainer applications and system settings.
              </p>
              <Link
                to="/dashboard/admin"
                className="inline-block rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Go to Dashboard
              </Link>
            </div>
          )}

          {/* Quick Links */}
          <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h3 className="mb-4 text-lg font-semibold text-gray-900">Quick Links</h3>
            <div className="space-y-2">
              <Link
                to="/bookings"
                className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Calendar className="h-5 w-5" />
                My Bookings
              </Link>
              <Link
                to="/dashboard"
                className="flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Settings className="h-5 w-5" />
                Dashboard
              </Link>
            </div>
          </div>

          {/* Sign Out */}
          <div className="rounded-xl border border-red-200 bg-red-50 p-6">
            <h3 className="mb-2 text-lg font-semibold text-red-900">Sign Out</h3>
            <p className="mb-4 text-sm text-red-700">
              Sign out of your account. You can sign back in anytime.
            </p>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-100"
            >
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
